import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Combobox } from '@/components/ui/combobox';
import { Plus, Trash2, CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/numberFormat';
import { toast } from 'sonner';
import { useContract, useFullUpdateContract } from '@/hooks/useContracts';
import { useBusinessUnitSearch } from '@/hooks/useBusinessUnits';
import { useSupplierInfiniteSearch } from '@/hooks/useSuppliers';
import { toSupplierOptions } from '@/lib/supplierOptions';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { AuditTrail } from '@/components/contracts/AuditTrail';
import { useProductSearch } from '@/hooks/useProducts';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

type ContractType = 'Volume' | 'Price' | 'BaisseClause';
type Mode = 'Single' | 'Several';

// Editable copy of a volume distribution. `id` is the server id (absent for rows added in this form),
// `key` is a stable React key.
interface DistributionDraft {
  key: string;
  id?: number;
  businessUnitId: number;
  businessUnitName: string;
  productName: string;
  supplierName: string;
  volumeKgs: number;
  incoTerm: string;
  incoTermPlace: string;
  pricePerKg: number;
  currency: string;
  shippingInstructions: string;
  confirmedBy?: string;
}

const emptyDistribution = (productName: string, supplierName: string): DistributionDraft => ({
  key: `new-${Date.now()}-${Math.random()}`,
  businessUnitId: 0,
  businessUnitName: '',
  productName,
  supplierName,
  volumeKgs: 0,
  incoTerm: '',
  incoTermPlace: '',
  pricePerKg: 0,
  currency: 'EUR',
  shippingInstructions: '',
});

// Comparable fingerprint of everything the form can change, used to detect unsaved edits.
const snapshotOf = (v: {
  name: string;
  contractDate?: Date;
  type: string;
  startDate?: Date;
  endDate?: Date;
  mode: string;
  distributions: DistributionDraft[];
}) =>
  JSON.stringify([
    v.name,
    v.contractDate?.getTime(),
    v.type,
    v.startDate?.getTime(),
    v.endDate?.getTime(),
    v.mode,
    v.distributions.map(({ key: _key, ...rest }) => rest),
  ]);

function DatePicker({ label, value, onChange }: { label: string; value?: Date; onChange: (d?: Date) => void }) {
  const id = `date-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} <span aria-hidden="true" className="text-destructive">*</span>
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className={cn('w-full justify-start text-left font-normal', !value && 'text-muted-foreground')}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            {value ? format(value, 'dd MMM yyyy') : 'dd mmm yyyy'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function EditContract() {
  const { id } = useParams();
  const contractId = parseInt(id || '0');
  const navigate = useNavigate();
  const { data: contract, isLoading, error } = useContract(contractId);
  const fullUpdate = useFullUpdateContract();

  const [supplierSearch, setSupplierSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [businessUnitSearch, setBusinessUnitSearch] = useState('');
  const debouncedSupplierSearch = useDebouncedValue(supplierSearch, 300);
  const debouncedProductSearch = useDebouncedValue(productSearch, 300);
  const debouncedBusinessUnitSearch = useDebouncedValue(businessUnitSearch, 300);

  const {
    data: supplierPages,
    isLoading: suppliersLoading,
    isFetchingNextPage: suppliersLoadingMore,
    hasNextPage: hasMoreSuppliers,
    fetchNextPage: fetchMoreSuppliers,
  } = useSupplierInfiniteSearch(debouncedSupplierSearch, 50);
  const { data: products = [], isLoading: productsLoading } = useProductSearch(debouncedProductSearch);
  const { data: businessUnits = [], isLoading: businessUnitsLoading } = useBusinessUnitSearch(debouncedBusinessUnitSearch);
  const suppliers = supplierPages?.pages.flatMap((page) => page.items) ?? [];

  const [contractName, setContractName] = useState('');
  const [contractDate, setContractDate] = useState<Date>();
  const [contractType, setContractType] = useState<ContractType>('Volume');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [mode, setMode] = useState<Mode>('Single');
  const [distributions, setDistributions] = useState<DistributionDraft[]>([]);
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<DistributionDraft | null>(null);

  // Populate the form once the contract has loaded
  useEffect(() => {
    if (!contract) return;
    const drafts: DistributionDraft[] = (contract.volumeDistributions ?? []).map((vd) => ({
      key: `vd-${vd.id}`,
      id: vd.id,
      businessUnitId: vd.businessUnitId,
      businessUnitName: vd.businessUnitName,
      productName: vd.productName,
      supplierName: vd.supplierName,
      volumeKgs: vd.volumeKgs,
      incoTerm: vd.incoTerm,
      incoTermPlace: vd.incoTermPlace,
      pricePerKg: vd.pricePerKg,
      currency: vd.currency,
      shippingInstructions: vd.shippingInstructions ?? '',
      confirmedBy: vd.confirmedBy,
    }));
    const loaded = {
      name: contract.contractName,
      contractDate: new Date(contract.contractDate),
      type: contract.contractType as ContractType,
      startDate: new Date(contract.startDate),
      endDate: new Date(contract.endDate),
      mode: contract.businessUnitMode as Mode,
      distributions: drafts,
    };
    setContractName(loaded.name);
    setContractDate(loaded.contractDate);
    setContractType(loaded.type);
    setStartDate(loaded.startDate);
    setEndDate(loaded.endDate);
    setMode(loaded.mode);
    setDistributions(drafts);
    setInitialSnapshot(snapshotOf(loaded));
  }, [contract]);

  const updateDistribution = <K extends keyof DistributionDraft>(key: string, field: K, value: DistributionDraft[K]) => {
    setDistributions((prev) => prev.map((d) => (d.key === key ? { ...d, [field]: value } : d)));
  };

  const addDistribution = () => {
    const first = distributions[0];
    setDistributions((prev) => [...prev, emptyDistribution(first?.productName ?? '', first?.supplierName ?? '')]);
  };

  const removeDistribution = (key: string) => {
    setDistributions((prev) => prev.filter((d) => d.key !== key));
  };

  // Rows that exist on the server are deleted on save, so ask first; rows added in this form just go away
  const requestRemoval = (d: DistributionDraft) => {
    if (d.id === undefined) removeDistribution(d.key);
    else setPendingRemoval(d);
  };

  // Switching to Single keeps only the first distribution; the others would be removed on save
  const handleModeChange = (next: Mode) => {
    if (next === 'Single' && distributions.length > 1) {
      toast.warning('Single mode keeps only the first business unit; the others are removed when you save.');
      setDistributions((prev) => prev.slice(0, 1));
    }
    setMode(next);
  };

  const totalVolume = distributions.reduce((sum, d) => sum + (d.volumeKgs || 0), 0);

  const isDirty = useMemo(
    () =>
      initialSnapshot !== null &&
      snapshotOf({ name: contractName, contractDate, type: contractType, startDate, endDate, mode, distributions }) !==
        initialSnapshot,
    [initialSnapshot, contractName, contractDate, contractType, startDate, endDate, mode, distributions]
  );

  // Warn before a reload or tab close throws away unsaved edits
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contractName.trim()) return toast.error('Please enter a contract name');
    if (!contractDate || !startDate || !endDate) return toast.error('Please select all dates');
    if (endDate <= startDate) return toast.error('End date must be after start date');
    if (distributions.length === 0) return toast.error('Please add at least one business unit');
    if (mode === 'Single' && distributions.length !== 1) {
      return toast.error('Single mode requires exactly one business unit');
    }
    const invalid = distributions.some(
      (d) =>
        !d.businessUnitId ||
        !d.productName.trim() ||
        !d.supplierName.trim() ||
        d.volumeKgs <= 0 ||
        d.pricePerKg <= 0 ||
        !d.incoTerm.trim() ||
        !d.incoTermPlace.trim()
    );
    if (invalid) return toast.error('Please complete all business unit details (volume and price must be above zero)');

    try {
      await fullUpdate.mutateAsync({
        id: contractId,
        data: {
          contractName: contractName.trim(),
          contractDate: contractDate.toISOString(),
          contractType,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          businessUnitMode: mode,
          volumeDistributions: distributions.map((d) => ({
            id: d.id,
            businessUnitId: d.businessUnitId,
            productName: d.productName.trim(),
            supplierName: d.supplierName.trim(),
            volumeKgs: d.volumeKgs,
            incoTerm: d.incoTerm.trim(),
            incoTermPlace: d.incoTermPlace.trim(),
            pricePerKg: d.pricePerKg,
            currency: d.currency,
            shippingInstructions: d.shippingInstructions,
            confirmedBy: d.confirmedBy,
          })),
        },
      });
      navigate(`/contracts/${contractId}`);
    } catch {
      // Error toast already shown by the hook
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6" aria-busy="true" aria-label="Loading contract">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  if (error || !contract) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold">Contract not found</h1>
          <Link to="/contracts">
            <Button className="mt-4">Back to Contracts</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (contract.isDeleted) {
    return (
      <Layout>
        <div className="text-center py-12 space-y-2">
          <h1 className="text-2xl font-bold">This contract is deleted</h1>
          <p className="text-muted-foreground">Restore it from the contracts list before editing.</p>
          <Link to="/contracts">
            <Button className="mt-4">Back to Contracts</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const buOptions = businessUnits.map((bu) => ({ value: bu.id.toString(), label: bu.name, sublabel: bu.businessUnitKey }));

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <ScrollPage
          framed={false}
          label="Contract form"
          footerBordered
          header={
            <PageHeader
              back={{ to: `/contracts/${contractId}`, label: 'Back to contract' }}
              title="Edit contract"
              description={
                <div className="space-y-2">
                  <p>{contract.contractName}</p>
                  <AuditTrail contract={contract} inline />
                </div>
              }
            />
          }
          footer={
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <p
                className={cn('flex items-center gap-2 text-sm', isDirty ? 'font-medium text-foreground' : 'text-muted-foreground')}
                role="status"
              >
                <span
                  aria-hidden="true"
                  className={cn('h-2 w-2 rounded-full', isDirty ? 'bg-amber-500' : 'bg-muted-foreground/40')}
                />
                {isDirty ? 'You have unsaved changes' : 'Make a change to enable saving'}
              </p>
              <div className="flex items-center gap-3">
                <Button asChild variant="outline">
                  <Link to={`/contracts/${contractId}`}>Cancel</Link>
                </Button>
                <Button type="submit" disabled={fullUpdate.isPending || !isDirty}>
                  {fullUpdate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save changes
                </Button>
              </div>
            </div>
          }
        >
          <div className="max-w-5xl space-y-6 pb-6">
        <section aria-labelledby="contract-heading" className="space-y-5 rounded-lg border bg-card p-5 sm:p-6">
          <h2 id="contract-heading" className="text-base font-semibold">Contract details</h2>

          <div className="space-y-2 md:max-w-xl">
            <Label htmlFor="contractName">Contract Name *</Label>
            <Input
              id="contractName"
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <DatePicker label="Contract Date" value={contractDate} onChange={setContractDate} />
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} />
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:items-end">
            <div className="space-y-2">
              <Label htmlFor="contractType">Contract Type *</Label>
              <Select value={contractType} onValueChange={(v) => setContractType(v as ContractType)}>
                <SelectTrigger id="contractType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Volume">Volume Contract</SelectItem>
                  <SelectItem value="Price">Price Contract</SelectItem>
                  <SelectItem value="BaisseClause">Baisse Clause Contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label id="assignment-label">Business Unit Assignment *</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={mode}
                onValueChange={(v) => v && handleModeChange(v as Mode)}
                aria-labelledby="assignment-label"
                className="grid grid-cols-2 gap-0"
              >
                <ToggleGroupItem value="Single" className="h-10 rounded-r-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary">Single</ToggleGroupItem>
                <ToggleGroupItem value="Several" className="h-10 rounded-l-none border-l-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary">Multiple</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </section>

        <section aria-labelledby="bu-heading" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <h2 id="bu-heading" className="text-base font-semibold">
                {mode === 'Single' ? 'Business unit' : 'Distribution over business units'}
              </h2>
              <div className="flex items-baseline gap-2 rounded-md border bg-card px-3 py-1.5">
                <span className="text-xs text-muted-foreground">Total volume</span>
                <span className="text-lg font-semibold tabular-nums">{formatNumber(totalVolume, 2)}</span>
                <span className="text-xs text-muted-foreground">kgs{mode === 'Several' && ' · all units'}</span>
              </div>
            </div>
            {mode === 'Several' && (
              <Button type="button" variant="outline" onClick={addDistribution}>
                <Plus className="mr-2 h-4 w-4" />
                Add business unit
              </Button>
            )}
          </div>

          {distributions.length === 0 && (
            <div className="rounded-lg border border-dashed px-6 py-10 text-center text-muted-foreground">
              No business units yet.{' '}
              <Button type="button" variant="link" className="h-auto p-0" onClick={addDistribution}>
                Add one
              </Button>
            </div>
          )}

          {distributions.map((d, index) => (
            <div
              key={d.key}
              role="group"
              aria-labelledby={`${d.key}-title`}
              className="space-y-5 rounded-lg border bg-card p-5 sm:p-6"
            >
              {/* Stays in view while scrolling through a long unit, so you always know which one you are editing */}
              <div className="sticky top-0 z-10 -mx-5 -mt-5 flex items-center justify-between gap-4 rounded-t-lg border-b bg-card px-5 py-3 sm:-mx-6 sm:-mt-6 sm:px-6">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <h3 id={`${d.key}-title`} className="truncate text-base font-semibold">
                    {d.businessUnitName || `Business unit ${index + 1}`}
                  </h3>
                  {d.id === undefined && <Badge variant="info">New</Badge>}
                  {mode === 'Several' && totalVolume > 0 && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatNumber(((d.volumeKgs || 0) / totalVolume) * 100, 1)}% of total volume
                    </span>
                  )}
                </div>
                {mode === 'Several' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => requestRemoval(d)}
                    className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove business unit ${index + 1}`}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>

              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Parties and product</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-bu`}>Business Unit *</Label>
                  <Combobox
                    id={`${d.key}-bu`}
                    value={d.businessUnitId ? d.businessUnitId.toString() : ''}
                    selectedLabel={d.businessUnitName}
                    onValueChange={(v) => {
                      const bu = businessUnits.find((b) => b.id.toString() === v);
                      updateDistribution(d.key, 'businessUnitId', parseInt(v));
                      if (bu) updateDistribution(d.key, 'businessUnitName', bu.name);
                    }}
                    onSearchChange={setBusinessUnitSearch}
                    placeholder="Search business units..."
                    emptyMessage="No business units found"
                    isLoading={businessUnitsLoading}
                    options={
                      // keep the current selection visible even when it is not in the search results
                      d.businessUnitId && !buOptions.some((o) => o.value === d.businessUnitId.toString())
                        ? [{ value: d.businessUnitId.toString(), label: d.businessUnitName, sublabel: '' }, ...buOptions]
                        : buOptions
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-supplier`}>Supplier *</Label>
                  <Combobox
                    id={`${d.key}-supplier`}
                    options={toSupplierOptions(suppliers)}
                    value={d.supplierName}
                    selectedLabel={d.supplierName}
                    onValueChange={(v) => updateDistribution(d.key, 'supplierName', v)}
                    onSearchChange={setSupplierSearch}
                    onLoadMore={() => {
                      if (hasMoreSuppliers && !suppliersLoadingMore) fetchMoreSuppliers();
                    }}
                    placeholder="Search and select supplier..."
                    searchPlaceholder="Type to search suppliers..."
                    emptyMessage="No suppliers found. Try a different search."
                    isLoading={suppliersLoading}
                    isLoadingMore={suppliersLoadingMore}
                    hasMore={!!hasMoreSuppliers}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-product`}>Product *</Label>
                  <Combobox
                    id={`${d.key}-product`}
                    options={products.map((p) => ({
                      value: p.name,
                      label: p.name,
                      sublabel: p.code + (p.category ? ` - ${p.category}` : ''),
                    }))}
                    value={d.productName}
                    selectedLabel={d.productName}
                    onValueChange={(v) => updateDistribution(d.key, 'productName', v)}
                    onSearchChange={setProductSearch}
                    placeholder="Search and select product..."
                    searchPlaceholder="Type to search products..."
                    emptyMessage="No products found. Try a different search."
                    isLoading={productsLoading}
                  />
                </div>
              </div>

              <p className="border-t pt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Volume and price</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-volume`}>Volume (kgs) *</Label>
                  <NumberInput
                    id={`${d.key}-volume`}
                    placeholder="0"
                    value={d.volumeKgs || 0}
                    onValueChange={(v) => updateDistribution(d.key, 'volumeKgs', Number(v))}
                    decimals={2}
                    className="text-right tabular-nums"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-price`}>Price per Kg *</Label>
                  <NumberInput
                    id={`${d.key}-price`}
                    placeholder="0.000"
                    value={d.pricePerKg || 0}
                    onValueChange={(v) => updateDistribution(d.key, 'pricePerKg', Number(v))}
                    decimals={3}
                    className="text-right tabular-nums"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-currency`}>Currency *</Label>
                  <Select value={d.currency} onValueChange={(v) => updateDistribution(d.key, 'currency', v)}>
                    <SelectTrigger id={`${d.key}-currency`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="border-t pt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivery</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-inco`}>Inco Term *</Label>
                  <Input
                    id={`${d.key}-inco`}
                    placeholder="e.g., FOB, CIF"
                    value={d.incoTerm}
                    onChange={(e) => updateDistribution(d.key, 'incoTerm', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${d.key}-inco-place`}>Inco Term Place *</Label>
                  <Input
                    id={`${d.key}-inco-place`}
                    placeholder="e.g., Rotterdam"
                    value={d.incoTermPlace}
                    onChange={(e) => updateDistribution(d.key, 'incoTermPlace', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${d.key}-shipping`}>Shipping Instructions</Label>
                <Textarea
                  id={`${d.key}-shipping`}
                  placeholder="Enter shipping instructions"
                  value={d.shippingInstructions}
                  onChange={(e) => updateDistribution(d.key, 'shippingInstructions', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          ))}
        </section>

          </div>
        </ScrollPage>
      </form>

      <ConfirmActionDialog
        open={!!pendingRemoval}
        destructive
        title="Remove business unit?"
        description={`"${pendingRemoval?.businessUnitName || 'This business unit'}" and its volume of ${formatNumber(pendingRemoval?.volumeKgs ?? 0, 2)} kgs will be removed from the contract when you save.`}
        confirmLabel="Remove"
        onConfirm={() => {
          if (pendingRemoval) removeDistribution(pendingRemoval.key);
          setPendingRemoval(null);
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </Layout>
  );
}
